import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SectionFormData, sectionSchema } from '@/lib/sectionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

export function SectionForm({
  onSubmit,
  onCancel,
  isSubmitting,
  initialData,
}: {
  onSubmit: (data: SectionFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialData?: Partial<SectionFormData>;
}) {
  const form = useForm<SectionFormData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: initialData || {
      section_name: '',
      description: '',
      section_order: undefined,
    },
  });
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="section_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Section Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Personal Information" {...field} />
              </FormControl>
              <FormDescription>
                A clear, descriptive name for this sections
              </FormDescription>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe what information this section will collect..."
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Help users understand what this section is about
              </FormDescription>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="section_order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Order</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="e.g., 1"
                  {...field}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  value={field.value}
                />
              </FormControl>
              <FormDescription>
                The order in which this section appears (lower numbers appear
                first)
              </FormDescription>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button type="button" variant={'outline'} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Section'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
